import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

interface Project {
  id: number
  title: string
  category: string
  shortDescription: string
  description: string
  client: string
  year: string
  tools: string[]
  images: string[]
  slug: string
}

const projectFiles = [
  'beigua-trophy.json',
  'dui-brand-system.json',
  'genova-pra-skatepark.json',
  'tribute-discovery-daft-punk.json',
  'typography-architecture.json',
  'lavazza-professional.json',
  'juventus.json',
  'ove.json',
  'iter.json',
  'fpt.json',
  'distinto.json',
  'photo.json'
]

export function GenericProject() {
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadProject = async () => {
      setLoading(true)
      setError(null)
      
      // Get current hash
      const hash = window.location.hash.slice(1)
      
      // Find matching project file
      const fileName = projectFiles.find(f => f.replace('.json', '') === hash)
      
      if (!fileName) {
        setError('Progetto non trovato')
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`/content/projects/${fileName}`)
        if (!response.ok) {
          setError('Errore nel caricamento')
          setLoading(false)
          return
        }
        const data = await response.json()
        setProject(data)
      } catch (err) {
        setError('Errore di connessione')
        console.error('Error loading project:', err)
      }
      setLoading(false)
    }
    
    loadProject()
  }, [])

  if (loading) {
    return (
      <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', paddingTop: '100px' }}>
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', paddingTop: '100px' }}>
        <div className="container mx-auto px-4">
          <a href="/#works" className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 mb-6">
            ← Torna ai progetti
          </a>
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
            {error || 'Progetto non trovato'}
          </h1>
        </div>
      </div>
    )
  }

  return (
    <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', paddingTop: '100px' }}>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <a 
            href="/#works" 
            className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 mb-6"
          >
            ← Torna ai progetti
          </a>
          
          <h1 style={{ fontSize: '3rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
            {project.title}
          </h1>
          <p style={{ color: '#6b7280', maxWidth: '42rem', marginBottom: '1rem' }}>
            {project.description}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {project.tools.map((tool, i) => (
              <span key={i} style={{ 
                padding: '0.25rem 0.75rem', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '9999px',
                fontSize: '0.875rem'
              }}>
                {tool}
              </span>
            ))}
          </div>
        </motion.div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1rem'
        }}>
          {project.images.map((img, imgIndex) => (
            <motion.div
              key={imgIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: imgIndex * 0.05 }}
              className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-200"
            >
              <img
                src={img}
                alt={`${project.title} - Immagine ${imgIndex + 1}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  )
}
